import React, { useEffect } from "react";
import { InputSizes } from "../../../../components/core/Input/Input";
import {
  Select,
  SelectVariantEnum,
} from "../../../../components/core/Select/Select";
import { IconSwitch } from "../../../../components/IconSwitch/IconSwitch";
import {
  TextSwitch,
  TextSwitchVariantEnum,
} from "../../../../components/TextSwitch/TextSwitch";
import { PermissionsEnum } from "../../../../constants/AppConstants";
import {
  PRACTICE_LABEL,
  COMPETENCE_CENTER_LABEL,
  GLOBAL_LABEL,
  COLLABORATORS_LABEL,
} from "../../../../constants/Dictionary";
import {
  MatrixViewModeV3,
  typeOptions,
} from "../../../../constants/PotentialMatrixConstants";
import { useAppSelector } from "../../../../hooks/store.hook";
import { PotentialMatrixFilters } from "../../../../hooks/usePotentialMatrixFilters.hook";
import { PotentialMatrixFilterKeyEnum } from "../../../../types/api/filters.type";
import { roleHasPermission } from "../../../../utils/roleAuth.utils";
import { ReactComponent as ChartIcon } from "../../../../icons/histogram.svg";
import { ReactComponent as CollaboratorIcon } from "../../../../icons/users.svg";
import { ReactComponent as SearchIcon } from "../../../../icons/search.svg";
import { ReactComponent as SearchActiveIcon } from "../../../../icons/searchActive.svg";
import { IconManager } from "../../../../components/core/IconManager/IconManager";
import {
  SearchCollaborators,
  type SearchCollaboratorsProps,
} from "../../../../components/SearchCollaborators/SearchCollaborators";
import { IconSizeEnum } from "../../../../types/AppTypes";
import { useState } from "react";
import {
  StyledInlineSearchPanel,
  StyledPotentialMatrixHeader,
  StyledSearchContainer,
} from "./PotentialMatrixHeader.styled";
import { usePracticeCompetenceData } from "../../../../hooks/potentialMatrix/usePracticeCompetenceData.hook";
import { InternalGradeChips } from "../../../PotentialMatrix/InternalGradesChips/InternalGradesChips";
import { usePotentialMatrixStats } from "../../../../hooks/potentialMatrix.hook";

export type PotentialMatrixCollaboratorSearchProps = Omit<
  SearchCollaboratorsProps,
  "toggleSearchInput"
> & {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  onClose: () => void;
};

export type PotentialMatrixHeaderProps = {
  filters: PotentialMatrixFilters;
  handleFilters: (
    key: PotentialMatrixFilterKeyEnum,
    value: PotentialMatrixFilters[PotentialMatrixFilterKeyEnum],
  ) => void;
  selectedViewMode?: MatrixViewModeV3;
  setSelectedViewMode?: (mode: MatrixViewModeV3) => void;
  isIconSwitchEnabled: boolean;
  collaboratorSearchProps?: PotentialMatrixCollaboratorSearchProps;
  previousYear?: number;
  showInternalGradeFilters: boolean;
  title?: React.ReactNode;
};

export const PotentialMatrixHeader = ({
  filters,
  handleFilters,
  selectedViewMode,
  setSelectedViewMode,
  isIconSwitchEnabled,
  collaboratorSearchProps,
  previousYear,
  showInternalGradeFilters,
  title,
}: PotentialMatrixHeaderProps) => {
  const { profile } = useAppSelector((state) => state.user);
  const [searchPracticeValue, setSearchPracticeValue] = useState("");
  const [searchCompetenceValue, setSearchCompetenceValue] = useState("");

  const { practiceList, CCList } = usePracticeCompetenceData(filters);

  const {
    data: potentialMatrixStatsData,
    isFetching: loadingPotentialMatrixStats,
  } = usePotentialMatrixStats({
    filters,
  });

  const filteredPracticeOptions =
    searchPracticeValue && practiceList
      ? practiceList.filter((option) =>
          option.name.toLowerCase().includes(searchPracticeValue.toLowerCase()),
        )
      : practiceList || [];

  const filteredCompetenceCenterOptions =
    searchCompetenceValue && CCList
      ? CCList.filter((option) =>
          option.name
            .toLowerCase()
            .includes(searchCompetenceValue.toLowerCase()),
        )
      : CCList || [];

  useEffect(() => {
    if (filters.cc.length === 0) return;

    const validCcIds = CCList.map((item) => item.id);
    const cleanedCc = filters.cc.filter((cc) => validCcIds.includes(cc));

    if (cleanedCc.length !== filters.cc.length) {
      handleFilters(PotentialMatrixFilterKeyEnum.COMPETENCE, cleanedCc);
    }
  }, [filters.practice]);

  const renderCollaboratorSearch = ({
    isVisible,
    setIsVisible,
    onClose,
    ...searchCollaboratorsProps
  }: PotentialMatrixCollaboratorSearchProps) => {
    const canShowSearch = selectedViewMode === MatrixViewModeV3.COLLABORATOR;

    return (
      <StyledSearchContainer $hidden={!canShowSearch}>
        <IconManager
          onClick={() => setIsVisible(true)}
          size={IconSizeEnum.MEDIUM}
        >
          {filters.collaboratorId ? <SearchActiveIcon /> : <SearchIcon />}
        </IconManager>

        <StyledInlineSearchPanel $visible={canShowSearch && isVisible}>
          <SearchCollaborators
            {...searchCollaboratorsProps}
            onClear={() => {
              searchCollaboratorsProps.onClear?.();
              setIsVisible(false);
            }}
            onSelectCollaborator={(collaboratorId, collaboratorLabel) => {
              searchCollaboratorsProps.onSelectCollaborator(
                collaboratorId,
                collaboratorLabel,
              );
              setIsVisible(false);
            }}
            toggleSearchInput={() => {
              onClose();
              setIsVisible(false);
            }}
          />
        </StyledInlineSearchPanel>
      </StyledSearchContainer>
    );
  };

  const showSubHeader = title || showInternalGradeFilters === true;

  return (
    <StyledPotentialMatrixHeader>
      <div className="main-header">
        <div className="filters-start-container">
          {roleHasPermission(
            profile,
            PermissionsEnum.VIEW_MATRIX_PRACTICE_SELECT,
          ) && (
            <div className="select-practice-container">
              <Select
                variant={SelectVariantEnum.MULTI_SELECT}
                size={InputSizes.MEDIUM}
                label={PRACTICE_LABEL}
                options={filteredPracticeOptions.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                value={filters.practice ?? ""}
                onChange={(value) =>
                  handleFilters(
                    PotentialMatrixFilterKeyEnum.PRACTICE,
                    value as string[],
                  )
                }
                withSearch
                onSearch={setSearchPracticeValue}
                searchValue={searchPracticeValue}
              />
            </div>
          )}
          {roleHasPermission(
            profile,
            PermissionsEnum.VIEW_MATRIX_COMPETENCE_SELECT,
          ) && (
            <div className="select-competence-center-container">
              <Select
                variant={SelectVariantEnum.MULTI_SELECT}
                label={COMPETENCE_CENTER_LABEL}
                size={InputSizes.MEDIUM}
                options={filteredCompetenceCenterOptions.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                value={filters.cc ?? ""}
                onChange={(value) =>
                  handleFilters(
                    PotentialMatrixFilterKeyEnum.COMPETENCE,
                    value as string[],
                  )
                }
                withSearch
                onSearch={setSearchCompetenceValue}
                searchValue={searchCompetenceValue}
              />
            </div>
          )}
        </div>
        <div className="switch-container">
          <TextSwitch
            options={
              filters.year === previousYear ? [typeOptions[1]] : typeOptions
            }
            selectedOption={filters.type}
            setSelectedOption={(typeOption) =>
              handleFilters(PotentialMatrixFilterKeyEnum.TYPE, typeOption)
            }
            variant={TextSwitchVariantEnum.SQUARED}
          />
        </div>
        <div className="filters-end-container">
          {collaboratorSearchProps &&
            renderCollaboratorSearch(collaboratorSearchProps)}
          {isIconSwitchEnabled && selectedViewMode && setSelectedViewMode && (
            <IconSwitch
              options={[
                {
                  value: MatrixViewModeV3.GLOBAL,
                  icon: <ChartIcon />,
                  tooltipLabel: GLOBAL_LABEL,
                },
                {
                  value: MatrixViewModeV3.COLLABORATOR,
                  icon: <CollaboratorIcon />,
                  tooltipLabel: COLLABORATORS_LABEL,
                },
              ]}
              selectedOption={selectedViewMode!}
              setSelectedOption={setSelectedViewMode!}
            />
          )}
        </div>
      </div>
      {showSubHeader && (
        <div className="sub-header">
          {title && <div className="title">{title}</div>}
          <InternalGradeChips
            visible={showInternalGradeFilters}
            value={filters.internalGrade}
            onChange={(value) =>
              handleFilters(PotentialMatrixFilterKeyEnum.INTERNAL_GRADE, value)
            }
            type={filters.type.value}
            potentialMatrixStatsData={potentialMatrixStatsData ?? []}
            loadingPotentialMatrixStats={loadingPotentialMatrixStats}
            alignFiltersToGrid={
              selectedViewMode === MatrixViewModeV3.COLLABORATOR
            }
          />
        </div>
      )}
    </StyledPotentialMatrixHeader>
  );
};
